import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PurchaseOrderItem = {
  id: string;
  productId: string;
  productName: string;
  requestedQty: number;
  receivedQty?: number | null;
  unit: 'LB' | 'KG' | 'PACK';
  expectedUnitPrice: number;
  finalUnitPrice?: number | null;
  lineTotalExpected: number;
  lineTotalFinal?: number | null;
};

type PurchaseOrderRow = {
  id: string;
  reference?: string | null;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  supplierName?: string | null;
  expectedDate?: string | null;
  completedAt?: string | null;
  createdAt: string;
  notes?: string | null;
  items: PurchaseOrderItem[];
  createdBy?: { id: string; email: string; employeeProfile?: { fullName?: string | null } | null } | null;
};

type PaginatedOrders = {
  items: PurchaseOrderRow[];
  total: number;
  page: number;
  pageSize: number;
};

type SupplierOption = {
  id: string;
  name: string;
  phone?: string | null;
  country?: string | null;
};

type ProductOption = {
  id: string;
  name: string;
  sku: string;
};
type PurchaseLineUnit = 'LB' | 'KG' | 'PACK';

function getCountryName(code?: string | null): string {
  if (!code) return '';
  try {
    if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
      const locale = navigator.language || 'en';
      const displayNames = new (Intl as any).DisplayNames([locale, 'en'], {
        type: 'region',
      });
      const name = displayNames.of(code);
      if (typeof name === 'string') return name;
    }
  } catch {
    // ignore
  }
  return code;
}

function PurchaseOrdersPage() {
  const { authorizedFetch } = useAuth();
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);

  const [orders, setOrders] = useState<PurchaseOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED'>('PENDING');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrderRow | null>(null);

  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');

  const [productQuery, setProductQuery] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [items, setItems] = useState<Array<{
    id?: string;
    productId: string;
    productName: string;
    requestedQty: string;
    unit: PurchaseLineUnit;
    expectedUnitPrice: string;
  }>>([]);
  const [departmentUnits, setDepartmentUnits] = useState<PurchaseLineUnit[]>(['LB', 'KG']);

  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    supplier?: string;
    items?: string;
  }>({});

  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrderRow | null>(null);

  const [completingOrder, setCompletingOrder] = useState<PurchaseOrderRow | null>(null);
  const [completionLines, setCompletionLines] = useState<Array<{
    id: string;
    productName: string;
    receivedQty: string;
    finalUnitPrice: string;
    expiryDate?: string;
  }>>([]);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completionSaving, setCompletionSaving] = useState(false);

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isAllScope = React.useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const scopedDepartmentSlug = React.useMemo(() => {
    if (isAllScope) return '';
    return String(workspaceScope || '').trim().toLowerCase();
  }, [isAllScope, workspaceScope]);
  const loadOrders = async (pageToLoad: number) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/purchase-orders`);
      url.searchParams.set('page', String(pageToLoad));
      url.searchParams.set('pageSize', String(pageSize));
      if (search) url.searchParams.set('q', search);
      if (fromDate) url.searchParams.set('from', fromDate);
      if (toDate) url.searchParams.set('to', toDate);
      if (statusFilter !== 'ALL') url.searchParams.set('status', statusFilter);
      if (scopedDepartmentSlug) {
        url.searchParams.set('departmentSlug', scopedDepartmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) throw new Error('Unable to load purchase orders.');
      const data = (await res.json()) as PaginatedOrders;
      setOrders(data.items);
      setTotal(data.total);
      setPage(data.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load purchase orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!scopedDepartmentSlug) {
        setDepartmentUnits(['LB', 'KG']);
        setItems((prev) =>
          prev.map((item) => ({
            ...item,
            unit: item.unit === 'PACK' ? 'LB' : item.unit,
          })),
        );
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE}/departments/public/${encodeURIComponent(scopedDepartmentSlug)}`,
        );
        if (!res.ok || cancelled) return;
        const dep = (await res.json()) as {
          productUnitLabels?: string[];
          defaultProductUnit?: PurchaseLineUnit;
        };
        const labels = Array.isArray(dep.productUnitLabels) ? dep.productUnitLabels : [];
        const normalized = labels.map((x) => String(x || '').trim().toLowerCase());
        const next = new Set<PurchaseLineUnit>();
        if (normalized.some((x) => ['lb', 'lbs', 'pound', 'pounds'].includes(x))) next.add('LB');
        if (normalized.some((x) => ['kg', 'kgs', 'kilogram', 'kilograms'].includes(x))) next.add('KG');
        if (normalized.some((x) => ['pack', 'packs', 'package', 'packages'].includes(x))) next.add('PACK');
        if (dep.defaultProductUnit) next.add(dep.defaultProductUnit);
        if (next.size === 0) {
          next.add('LB');
          next.add('KG');
        }
        const options = Array.from(next);
        if (!cancelled) {
          setDepartmentUnits(options);
          setItems((prev) =>
            prev.map((item) => ({
              ...item,
              unit: options.includes(item.unit) ? item.unit : options[0],
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setDepartmentUnits(['LB', 'KG']);
          setItems((prev) =>
            prev.map((item) => ({
              ...item,
              unit: item.unit === 'PACK' ? 'LB' : item.unit,
            })),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopedDepartmentSlug]);

  useEffect(() => {
    if (!supplierQuery || supplierId) {
      setSupplierOptions([]);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSupplierSearchLoading(true);
      try {
        const url = new URL(`${API_BASE}/suppliers`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '20');
        url.searchParams.set('q', supplierQuery);
        url.searchParams.set('activeOnly', 'true');
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          items: { id: string; name: string; phone?: string | null; country?: string | null }[];
        };
        setSupplierOptions(
          data.items.map((s) => ({
            id: s.id,
            name: s.name,
            phone: s.phone ?? null,
            country: s.country ?? null,
          })),
        );
      } catch {
        if (!cancelled) {
          setSupplierOptions([]);
        }
      } finally {
        if (!cancelled) {
          setSupplierSearchLoading(false);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [authorizedFetch, supplierQuery, supplierId]);

  useEffect(() => {
    if (!productQuery) {
      setProductOptions([]);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const url = new URL(`${API_BASE}/catalog/products`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '20');
        url.searchParams.set('q', productQuery);
        // For purchase orders, product suggestions should come from the catalog,
        // not only from currently in-stock/non-expired inventory.
        if (scopedDepartmentSlug) {
          url.searchParams.set('departmentSlug', scopedDepartmentSlug);
        }
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { items: { id: string; name: string; sku: string }[] };
        setProductOptions(
          data.items.map((p) => ({
            id: p.id,
            name: p.name,
            sku: p.sku,
          })),
        );
      } catch {
        if (!cancelled) {
          setProductOptions([]);
        }
      } finally {
        if (!cancelled) {
          setProductSearchLoading(false);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [authorizedFetch, productQuery, scopedDepartmentSlug]);

  const resetForm = () => {
    setEditingOrder(null);
    setSupplierQuery('');
    setSupplierId(null);
    setExpectedDate('');
    setNotes('');
    setItems([]);
    setFormErrors({});
  };

  const startCreate = () => {
    if (isAllScope) return;
    resetForm();
    setShowForm(true);
  };

  const startEdit = (order: PurchaseOrderRow) => {
    setEditingOrder(order);
    setSupplierQuery(order.supplierName ?? '');
    // We don't know the supplierId from the order payload; force explicit re-selection if needed.
    setSupplierId(null);
    setExpectedDate(order.expectedDate ? order.expectedDate.slice(0, 10) : '');
    setNotes(order.notes ?? '');
    setItems(
      order.items.map((i) => ({
        id: i.id,
        productId: i.productId,
        productName: i.productName,
        requestedQty: String(i.requestedQty),
        unit: i.unit,
        expectedUnitPrice: String(i.expectedUnitPrice),
      })),
    );
    setShowForm(true);
  };

  const addItemFromProduct = (product: ProductOption) => {
    setItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: `${product.name} (${product.sku})`,
        requestedQty: '',
        unit: departmentUnits[0] || 'LB',
        expectedUnitPrice: '',
      },
    ]);
    setProductQuery('');
    setProductOptions([]);
  };

  const startComplete = (order: PurchaseOrderRow) => {
    setCompletingOrder(order);
    setCompletionLines(
      order.items.map((i) => ({
        id: i.id,
        productName: i.productName,
        receivedQty: String(i.receivedQty ?? i.requestedQty),
        finalUnitPrice: String(i.finalUnitPrice ?? i.expectedUnitPrice),
        expiryDate: '',
      })),
    );
    setCompletionError(null);
  };

  const handleCompleteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completingOrder) return;
    setCompletionSaving(true);
    setCompletionError(null);
    try {
      const parsed = completionLines.map((line) => ({
        id: line.id,
        receivedQty: Number(line.receivedQty),
        finalUnitPrice: Number(line.finalUnitPrice),
        expiryDate: line.expiryDate ? new Date(line.expiryDate).toISOString() : undefined,
      }));
      if (
        parsed.some(
          (l) => Number.isNaN(l.receivedQty) || Number.isNaN(l.finalUnitPrice) || l.receivedQty <= 0 || l.finalUnitPrice <= 0,
        )
      ) {
        setCompletionError(
          t(
            'admin.po_complete_invalid',
            'Received quantity and final price must be greater than zero for all lines.',
          ),
        );
        setCompletionSaving(false);
        return;
      }

      const payload = {
        items: parsed,
      };

      const res = await authorizedFetch(
        `${API_BASE}/purchase-orders/${completingOrder.id}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message
            || t('backoffice.po_complete_error', 'Unable to complete purchase order.'),
        );
      }
      setCompletingOrder(null);
      setCompletionLines([]);
      await loadOrders(page);
    } catch (err) {
      setCompletionError(
        err instanceof Error
          ? err.message
          : t('backoffice.po_complete_error', 'Unable to complete purchase order.'),
      );
    } finally {
      setCompletionSaving(false);
    }
  };

  const handlePrint = async (order: PurchaseOrderRow) => {
    try {
      const res = await authorizedFetch(`${API_BASE}/purchase-orders/${order.id}/print`);
      if (!res.ok) throw new Error('Unable to generate PDF.');
      const data = (await res.json()) as {
        ok: boolean;
        filename: string;
        contentType: string;
        data: string;
      };
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('backoffice.po_print_error', 'Unable to generate purchase order PDF.'),
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    setError(null);
    try {
      const errors: { supplier?: string; items?: string } = {};
      if (!supplierId) {
        errors.supplier = t('backoffice.po_supplier_required', 'Please select a supplier.');
      }
      if (items.length === 0) {
        errors.items = t('backoffice.po_items_required', 'Please add at least one product.');
      }

      const parsedItems = items.map((item) => ({
        productId: item.productId,
        requestedQty: Number(item.requestedQty),
        unit: item.unit,
        expectedUnitPrice: Number(item.expectedUnitPrice),
      }));

      if (
        parsedItems.some(
          (i) => !i.productId || i.requestedQty <= 0 || i.expectedUnitPrice <= 0 || Number.isNaN(i.requestedQty) || Number.isNaN(i.expectedUnitPrice),
        )
      ) {
        errors.items = t('backoffice.po_items_invalid', 'Each line must have quantity and unit price greater than zero.');
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setSaving(false);
        return;
      }

      const payload = {
        supplierId,
        expectedDate: expectedDate || undefined,
        notes: notes || undefined,
        items: parsedItems,
      };

      const url = editingOrder
        ? `${API_BASE}/purchase-orders/${editingOrder.id}`
        : `${API_BASE}/purchase-orders`;
      const method = editingOrder ? 'PATCH' : 'POST';

      const res = await authorizedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Unable to save purchase order.');
      }

      resetForm();
      setShowForm(false);
      await loadOrders(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save purchase order.');
    } finally {
      setSaving(false);
    }
  };

  const { pathname } = location;

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={pathname}
    >
      <main className="workspace-page">
        <section className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.po_title', 'Purchase orders')}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {t('backoffice.po_subtitle', 'Plan and track supplier orders before receiving stock.')}
            </p>
          </div>
        </section>

        {error && (
          <p className="text-xs text-red-500">
            {error}
          </p>
        )}

        <section className="workspace-panel">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-1 flex-wrap items-end gap-3">
              <input
                type="search"
                placeholder={t('backoffice.po_search', 'Search by supplier or reference')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:w-64"
              />
              <div className="flex items-center gap-2 text-xs">
                <div className="space-y-1">
                  <span className="block text-[11px] font-medium text-slate-600">
                    {t('backoffice.po_from', 'From')}
                  </span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  />
                </div>
                <div className="space-y-1">
                  <span className="block text-[11px] font-medium text-slate-600">
                    {t('backoffice.po_to', 'To')}
                  </span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  />
                </div>
                <div className="space-y-1">
                  <span className="block text-[11px] font-medium text-slate-600">
                    {t('backoffice.po_status', 'Status')}
                  </span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'PENDING' | 'COMPLETED')}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  >
                    <option value="ALL">{t('backoffice.po_status_all', 'All')}</option>
                    <option value="PENDING">{t('backoffice.po_status_pending', 'Pending')}</option>
                    <option value="COMPLETED">{t('backoffice.po_status_completed', 'Completed')}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void loadOrders(1)}
                className="rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                {t('backoffice.po_search_button', 'Search')}
              </button>
              <button
                type="button"
                onClick={startCreate}
                disabled={isAllScope}
                title={
                  isAllScope
                    ? t('backoffice.po_scope_required', 'Select a specific department scope first.')
                    : undefined
                }
                className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('backoffice.po_new', 'New purchase order')}
              </button>
            </div>
          </div>
          {isAllScope && (
            <p className="mb-4 text-xs text-amber-700">
              {t(
                'backoffice.po_scope_required',
                'Select a specific department scope first.',
              )}
            </p>
          )}

          {showForm && (
            <form
              onSubmit={handleSubmit}
              className="mb-6 grid gap-3 border-b border-slate-100 pb-5 text-xs md:grid-cols-3"
            >
              <div className="space-y-1 md:col-span-2">
                <label className="block font-medium text-slate-700" htmlFor="poSupplier">
                  {t('backoffice.po_supplier', 'Supplier')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <div className="space-y-1">
                  <input
                    id="poSupplier"
                    type="text"
                    value={supplierQuery}
                    onChange={(e) => {
                      setSupplierQuery(e.target.value);
                      setSupplierId(null);
                    }}
                    placeholder={t('backoffice.po_supplier_placeholder', 'Search supplier by name')}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                  />
                  {supplierSearchLoading && (
                    <p className="mt-1 text-[11px] text-slate-500">
                      {t('backoffice.suppliers_loading', 'Loading suppliers…')}
                    </p>
                  )}
                  {supplierOptions.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white text-[11px] shadow-sm">
                      {supplierOptions.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setSupplierId(s.id);
                            const details = [
                              s.phone || null,
                              s.country ? getCountryName(s.country) : null,
                            ]
                              .filter(Boolean)
                              .join(' • ');
                            setSupplierQuery(
                              details ? `${s.name} (${details})` : s.name,
                            );
                            setSupplierOptions([]);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-slate-50"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">{s.name}</span>
                            <span className="text-slate-500">
                              {s.phone || '—'}
                              {s.country && (
                                <>
                                  {' • '}
                                  {getCountryName(s.country)}
                                </>
                              )}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {formErrors.supplier && (
                    <p className="mt-1 text-[11px] text-red-500">
                      {formErrors.supplier}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1 md:col-span-1">
                <label className="block font-medium text-slate-700" htmlFor="poExpectedDate">
                  {t('backoffice.po_expected_date', 'Expected date')}
                </label>
                <input
                  id="poExpectedDate"
                  type="date"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
              </div>
              {/* Reference is auto-generated by backend; no input needed */}
              <div className="space-y-1 md:col-span-2">
                <label className="block font-medium text-slate-700" htmlFor="poNotes">
                  {t('backoffice.po_notes', 'Notes')}
                </label>
                <input
                  id="poNotes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
              </div>

              <div className="space-y-1 md:col-span-3">
                <label className="block font-medium text-slate-700" htmlFor="poProductSearch">
                  {t('backoffice.po_items', 'Items')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  id="poProductSearch"
                  type="text"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder={t('backoffice.po_product_placeholder', 'Search product by name or SKU')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
                {productSearchLoading && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    {t('backoffice.inventory_lots_loading', 'Loading stock lots…')}
                  </p>
                )}
                {productOptions.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white text-[11px] shadow-sm">
                    {productOptions.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addItemFromProduct(p)}
                        className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-slate-50"
                      >
                        <span>{p.name}</span>
                        <span className="text-slate-400">{p.sku}</span>
                      </button>
                    ))}
                  </div>
                )}

                {items.length > 0 && (
                  <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-left text-[11px]">
                      <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="h-10 align-middle py-2 pl-3 pr-2">
                            {t('backoffice.po_col_product', 'Product')}
                          </th>
                          <th className="h-10 align-middle py-2 px-2 text-right">
                            {t('backoffice.po_col_qty', 'Qty')}
                          </th>
                          <th className="h-10 align-middle py-2 px-2">
                            {t('backoffice.po_col_unit', 'Unit')}
                          </th>
                          <th className="h-10 align-middle py-2 px-2 text-right">
                            {t('backoffice.po_col_price', 'Unit price')}
                          </th>
                          <th className="h-10 align-middle py-2 px-2 text-right">
                            {t('backoffice.po_col_total', 'Total')}
                          </th>
                          <th className="h-10 align-middle py-2 px-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, idx) => {
                          const qty = Number(item.requestedQty) || 0;
                          const price = Number(item.expectedUnitPrice) || 0;
                          const lineTotal = qty * price;
                          return (
                            <tr key={item.productId + idx}>
                              <td className="h-12 align-middle py-2 pl-3 pr-2 text-slate-800">
                                {item.productName}
                              </td>
                              <td className="h-12 align-middle py-2 px-2 text-right">
                                <div className="flex justify-end">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.requestedQty}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setItems((prev) =>
                                        prev.map((it, i) =>
                                          i === idx ? { ...it, requestedQty: value } : it,
                                        ),
                                      );
                                    }}
                                    className="h-9 w-20 rounded border border-slate-300 px-2 text-right"
                                  />
                                </div>
                              </td>
                              <td className="h-12 align-middle py-2 px-2">
                                <select
                                  value={item.unit}
                                  onChange={(e) => {
                                    const value = e.target.value as PurchaseLineUnit;
                                    setItems((prev) =>
                                      prev.map((it, i) =>
                                        i === idx ? { ...it, unit: value } : it,
                                      ),
                                    );
                                  }}
                                  className="h-9 rounded border border-slate-300 px-2"
                                >
                                  {departmentUnits.map((unit) => (
                                    <option key={unit} value={unit}>
                                      {unit.toLowerCase()}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="h-12 align-middle py-2 px-2 text-right">
                                <div className="flex justify-end">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.expectedUnitPrice}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setItems((prev) =>
                                        prev.map((it, i) =>
                                          i === idx ? { ...it, expectedUnitPrice: value } : it,
                                        ),
                                      );
                                    }}
                                    className="h-9 w-24 rounded border border-slate-300 px-2 text-right"
                                  />
                                </div>
                              </td>
                              <td className="h-12 align-middle py-2 px-2 text-right text-slate-800">
                                {lineTotal > 0 ? formatPrice(lineTotal) : '—'}
                              </td>
                              <td className="h-12 align-middle py-2 px-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setItems((prev) => prev.filter((_, i) => i !== idx));
                                  }}
                                  className="text-[11px] text-red-500 hover:underline"
                                >
                                  {t('common.remove', 'Remove')}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {formErrors.items && (
                  <p className="mt-1 text-[11px] text-red-500">
                    {formErrors.items}
                  </p>
                )}
              </div>

              <div className="flex items-end md:col-span-3 justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? t('common.saving', 'Saving…')
                    : editingOrder
                      ? t('backoffice.po_update', 'Update order')
                      : t('backoffice.po_save', 'Save order')}
                </button>
              </div>
            </form>
          )}

          {completingOrder && (
            <form
              onSubmit={handleCompleteSubmit}
              className="mb-6 mt-4 grid gap-3 border-b border-slate-100 pb-5 text-xs md:grid-cols-3"
            >
              <div className="md:col-span-3 flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-900">
                  {t('backoffice.po_complete_title', 'Complete purchase order')}{' '}
                  {completingOrder.reference || completingOrder.id.slice(-6)}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setCompletingOrder(null);
                    setCompletionLines([]);
                    setCompletionError(null);
                  }}
                  className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
              <div className="md:col-span-3">
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-left text-[11px]">
                    <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="py-1.5 pl-3 pr-2">
                          {t('backoffice.po_col_product', 'Product')}
                        </th>
                        <th className="py-1.5 px-2">
                          {t('backoffice.po_col_expiry_date', 'Expiry date')}
                        </th>
                        <th className="py-1.5 px-2 text-right">
                          {t('backoffice.po_col_received', 'Received qty')}
                        </th>
                        <th className="py-1.5 px-2 text-right">
                          {t('backoffice.po_col_final_price', 'Final unit price')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {completionLines.map((line, idx) => (
                        <tr key={line.id}>
                          <td className="py-1.5 pl-3 pr-2 text-slate-800">
                            {line.productName}
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="date"
                              value={line.expiryDate || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setCompletionLines((prev) =>
                                  prev.map((ln, i) =>
                                    i === idx ? { ...ln, expiryDate: value } : ln,
                                  ),
                                );
                              }}
                              className="h-9 w-40 rounded border border-slate-300 px-2"
                            />
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.receivedQty}
                              onChange={(e) => {
                                const value = e.target.value;
                                setCompletionLines((prev) =>
                                  prev.map((ln, i) =>
                                    i === idx ? { ...ln, receivedQty: value } : ln,
                                  ),
                                );
                              }}
                              className="ml-auto block h-9 w-40 rounded border border-slate-300 px-2 text-right"
                            />
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.finalUnitPrice}
                              onChange={(e) => {
                                const value = e.target.value;
                                setCompletionLines((prev) =>
                                  prev.map((ln, i) =>
                                    i === idx ? { ...ln, finalUnitPrice: value } : ln,
                                  ),
                                );
                              }}
                              className="ml-auto block h-9 w-40 rounded border border-slate-300 px-2 text-right"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {completionError && (
                  <p className="mt-1 text-[11px] text-red-500">
                    {completionError}
                  </p>
                )}
              </div>
              <div className="md:col-span-3 flex justify-end">
                <button
                  type="submit"
                  disabled={completionSaving}
                  className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {completionSaving
                    ? t('backoffice.po_completing', 'Completing…')
                    : t('backoffice.po_complete_action', 'Mark as completed')}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.po_loading', 'Loading purchase orders…')}
              skeletonRows={5}
            />
          ) : orders.length === 0 ? (
            <p className="text-sm text-slate-600">
              {t('backoffice.po_empty', 'No purchase orders found.')}
            </p>
          ) : (
            <>
              <div>
                <table className="w-full text-left text-[11px] sm:text-xs">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-3 whitespace-nowrap">
                        {t('backoffice.po_col_ref', 'Reference')}
                      </th>
                      <th className="py-2 pr-3">
                        {t('backoffice.po_col_supplier', 'Supplier')}
                      </th>
                      <th className="py-2 pr-3 whitespace-nowrap">
                        {t('backoffice.po_col_status', 'Status')}
                      </th>
                      <th className="py-2 pr-3 whitespace-nowrap">
                        {t('backoffice.po_col_expected', 'Expected')}
                      </th>
                      <th className="py-2 pr-3 whitespace-nowrap">
                        {t('backoffice.po_col_completed', 'Completed')}
                      </th>
                      <th className="py-2 pr-3 text-right">
                        {t('backoffice.po_col_total_expected', 'Total (expected)')}
                      </th>
                      <th className="py-2 pr-3 text-right">
                        {t('backoffice.po_col_total_final', 'Total (final)')}
                      </th>
                      <th className="py-2 pr-3">
                        {t('backoffice.po_col_employee', 'Employee')}
                      </th>
                      <th className="py-2 pr-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o) => {
                      const totalExpected = o.items.reduce(
                        (sum, line) => sum + Number(line.lineTotalExpected ?? 0),
                        0,
                      );
                      const totalFinal = o.items.reduce(
                        (sum, line) => sum + Number(line.lineTotalFinal ?? 0),
                        0,
                      );
                      void totalFinal;
                      return (
                        <tr key={o.id}>
                          <td className="py-2 pr-3 text-slate-900 whitespace-nowrap">
                            {o.reference || '—'}
                          </td>
                          <td className="py-2 pr-3 text-slate-700">
                            {o.supplierName || '—'}
                          </td>
                          <td className="py-2 pr-3">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${
                                o.status === 'COMPLETED'
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {o.status === 'COMPLETED'
                                ? t('backoffice.po_status_completed', 'Completed')
                                : t('backoffice.po_status_pending', 'Pending')}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">
                            {o.expectedDate
                              ? new Date(o.expectedDate).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">
                            {o.completedAt
                              ? new Date(o.completedAt).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="py-2 pr-3 text-right text-slate-700 whitespace-nowrap">
                            {totalExpected > 0 ? formatPrice(totalExpected) : '—'}
                          </td>
                          <td className="py-2 pr-3 text-right text-slate-700 whitespace-nowrap">
                            {o.items.reduce((sum, line) => sum + Number(line.lineTotalFinal ?? 0), 0) > 0
                              ? formatPrice(o.items.reduce((sum, line) => sum + Number(line.lineTotalFinal ?? 0), 0))
                              : '—'}
                          </td>
                          <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">
                            {o.createdBy
                              ? o.createdBy.email.split('@')[0]
                              : '—'}
                          </td>
                          <td className="py-2 pr-2">
                            <div className="inline-flex overflow-hidden rounded-full border border-slate-300 bg-white shadow-sm text-[10px]">
                              <button
                                type="button"
                                onClick={() => handlePrint(o)}
                                className="px-2.5 py-1 font-medium uppercase tracking-wide text-slate-700 hover:bg-slate-50 border-r border-slate-200"
                              >
                                {t('backoffice.po_print', 'Print')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedOrder(o)}
                                className="px-2.5 py-1 font-medium uppercase tracking-wide text-slate-700 hover:bg-slate-50 border-r border-slate-200"
                              >
                                {t('backoffice.po_view', 'View')}
                              </button>
                              {o.status === 'PENDING' && (
                                <button
                                  type="button"
                                  onClick={() => startEdit(o)}
                                  className="px-2.5 py-1 font-medium uppercase tracking-wide text-slate-700 hover:bg-slate-50 border-r border-slate-200"
                                >
                                  {t('backoffice.po_edit', 'Edit')}
                                </button>
                              )}
                              {o.status === 'PENDING' && (
                                <button
                                  type="button"
                                  onClick={() => startComplete(o)}
                                  disabled={isAllScope}
                                  className="px-2.5 py-1 font-medium uppercase tracking-wide text-emerald-700 hover:bg-emerald-50"
                                >
                                  {t('backoffice.po_mark_completed', 'Complete')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-600">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => void loadOrders(page - 1)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.inventory_prev_page', 'Prev')}
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => void loadOrders(page + 1)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.inventory_next_page', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      {selectedOrder && (
        <section className="glass-panel mt-4 p-6 sm:p-8">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                {t('backoffice.po_detail_title', 'Purchase order detail')}
              </h2>
              <p className="text-xs text-slate-500">
                {selectedOrder.reference || selectedOrder.id}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedOrder(null)}
              className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
            >
              {t('common.close', 'Close')}
            </button>
          </div>
          <div className="mb-4 grid gap-3 text-xs sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('backoffice.po_detail_supplier', 'Supplier')}
              </div>
              <div className="mt-0.5 text-slate-900">
                {selectedOrder.supplierName || '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('backoffice.po_detail_status', 'Status')}
              </div>
              <div className="mt-0.5 text-slate-900">
                {selectedOrder.status === 'COMPLETED'
                  ? t('backoffice.po_status_completed', 'Completed')
                  : t('backoffice.po_status_pending', 'Pending')}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('backoffice.po_expected_date', 'Expected date')}
              </div>
              <div className="mt-0.5 text-slate-900">
                {selectedOrder.expectedDate
                  ? new Date(selectedOrder.expectedDate).toLocaleDateString()
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('backoffice.po_completed_date', 'Completed date')}
              </div>
              <div className="mt-0.5 text-slate-900">
                {selectedOrder.completedAt
                  ? new Date(selectedOrder.completedAt).toLocaleDateString()
                  : '—'}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-left text-[11px] sm:text-xs">
              <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1.5 pl-3 pr-2">
                    {t('backoffice.po_col_product', 'Product')}
                  </th>
                  <th className="py-1.5 px-2 text-right">
                    {t('backoffice.po_col_qty', 'Qty (requested)')}
                  </th>
                  <th className="py-1.5 px-2 text-right">
                    {t('backoffice.po_col_received', 'Qty (received)')}
                  </th>
                  <th className="py-1.5 px-2 text-right">
                    {t('backoffice.po_col_price', 'Expected price')}
                  </th>
                  <th className="py-1.5 px-2 text-right">
                    {t('backoffice.po_col_final_price', 'Final price')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selectedOrder.items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-1.5 pl-3 pr-2 text-slate-800">
                      {i.productName}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-700">
                      {i.requestedQty.toFixed(2)} {i.unit.toLowerCase()}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-700">
                      {i.receivedQty != null ? i.receivedQty.toFixed(2) : '—'}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-700">
                      {Number(i.expectedUnitPrice).toFixed(2)}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-700">
                      {i.finalUnitPrice != null
                        ? Number(i.finalUnitPrice).toFixed(2)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const totalExpected = selectedOrder.items.reduce(
              (sum, line) => sum + Number(line.lineTotalExpected ?? 0),
              0,
            );
            const totalFinal = selectedOrder.items.reduce(
              (sum, line) => sum + Number(line.lineTotalFinal ?? 0),
              0,
            );
            const expectedQty = selectedOrder.items.reduce(
              (sum, line) => sum + Number(line.requestedQty ?? 0),
              0,
            );
            const finalQty = selectedOrder.items.reduce(
              (sum, line) => sum + Number(line.receivedQty ?? line.requestedQty ?? 0),
              0,
            );
            const hasAmount = totalExpected > 0 && totalFinal > 0;
            const hasQty = expectedQty > 0 && finalQty > 0;
            if (!hasAmount && !hasQty) return null;

            const deltaAmount = hasAmount ? totalFinal - totalExpected : 0;
            const deltaAmountPercent = hasAmount ? (deltaAmount / totalExpected) * 100 : 0;
            const deltaQty = hasQty ? finalQty - expectedQty : 0;
            const deltaQtyPercent = hasQty ? (deltaQty / expectedQty) * 100 : 0;

            return (
              <p className="mt-3 text-[11px] text-slate-600">
                {hasAmount && (
                  <>
                    {t('backoffice.po_insight_amount_prefix', 'Amount variance:')}{' '}
                    <span
                      className={
                        deltaAmount > 0
                          ? 'font-semibold text-red-600'
                          : deltaAmount < 0
                            ? 'font-semibold text-emerald-600'
                            : 'font-semibold text-slate-700'
                      }
                    >
                      {deltaAmount > 0 ? '+' : deltaAmount < 0 ? '-' : '±'}
                      {formatPrice(Math.abs(deltaAmount))} (
                      {deltaAmount > 0 ? '+' : deltaAmount < 0 ? '-' : '±'}
                      {Math.abs(deltaAmountPercent).toFixed(1)}%)
                    </span>
                  </>
                )}
                {hasAmount && hasQty ? ' • ' : ''}
                {hasQty && (
                  <>
                    {t('backoffice.po_insight_qty_prefix', 'Quantity variance:')}{' '}
                    <span
                      className={
                        deltaQty > 0
                          ? 'font-semibold text-red-600'
                          : deltaQty < 0
                            ? 'font-semibold text-emerald-600'
                            : 'font-semibold text-slate-700'
                      }
                    >
                      {deltaQty > 0 ? '+' : deltaQty < 0 ? '-' : '±'}
                      {Math.abs(deltaQty).toFixed(2)} (
                      {deltaQty > 0 ? '+' : deltaQty < 0 ? '-' : '±'}
                      {Math.abs(deltaQtyPercent).toFixed(1)}%)
                    </span>
                  </>
                )}
              </p>
            );
          })()}
        </section>
      )}
    </MainLayout>
  );
}

export default PurchaseOrdersPage;

